// src/components/tournament-manage/tournament-manage.component.ts
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { TournamentService } from '../../services/tournament.service';
import { Tournament, TournamentStatus, Bracket, BracketMatch } from '../../model/tournament';
import { TournamentBracketComponent } from '../tournament-bracket/tournament-bracket.component';

interface SetScore { a: number | null; b: number | null; }

@Component({
  selector: 'app-tournament-manage',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, LucideAngularModule, TournamentBracketComponent],
  templateUrl: './tournament-manage.component.html',
  styleUrl: './tournament-manage.component.css',
})
export class TournamentManageComponent implements OnInit {
  tournamentId!: number;
  tournament: Tournament | null = null;
  bracket: Bracket | null = null;
  loading = true;
  submitting = false;
  error = '';

  TournamentStatus = TournamentStatus;

  // result modal state
  activeMatch: BracketMatch | null = null;
  winnerTeamId: number | null = null;
  isWalkover = false;
  sets: SetScore[] = [{ a: null, b: null }, { a: null, b: null }, { a: null, b: null }];

  constructor(
    private route: ActivatedRoute,
    private tournamentService: TournamentService,
  ) {}

  ngOnInit(): void {
    this.tournamentId = Number(this.route.snapshot.params['id']);
    this.reload();
  }

  private reload(): void {
    this.tournamentService.getById(this.tournamentId).subscribe({
      next: t => {
        this.tournament = t;
        this.loading = false;
        if (t.status >= TournamentStatus.Drawn) this.loadBracket();
        else this.bracket = null;
      },
      error: () => { this.loading = false; this.error = 'Failed to load tournament.'; },
    });
  }

  private loadBracket(): void {
    this.tournamentService.getBracket(this.tournamentId).subscribe({
      next: b => { this.bracket = b; },
      error: () => { this.bracket = null; },
    });
  }

  get canDraw(): boolean {
    return this.tournament?.status === TournamentStatus.ReadyToDraw;
  }

  get canCancel(): boolean {
    return !!this.tournament
      && this.tournament.status !== TournamentStatus.Completed
      && this.tournament.status !== TournamentStatus.Cancelled;
  }

  generateDraw(): void {
    if (!this.tournament) return;
    this.submitting = true;
    this.error = '';
    this.tournamentService.draw(this.tournamentId).subscribe({
      next: () => { this.submitting = false; this.reload(); },
      error: err => { this.submitting = false; this.error = this.readError(err); },
    });
  }

  cancelTournament(): void {
    if (!this.tournament) return;
    this.submitting = true;
    this.error = '';
    this.tournamentService.cancelTournament(this.tournamentId).subscribe({
      next: () => { this.submitting = false; this.reload(); },
      error: err => { this.submitting = false; this.error = this.readError(err); },
    });
  }

  openResult(m: BracketMatch): void {
    this.activeMatch = m;
    this.winnerTeamId = null;
    this.isWalkover = false;
    this.sets = [{ a: null, b: null }, { a: null, b: null }, { a: null, b: null }];
  }

  closeResult(): void {
    this.activeMatch = null;
  }

  submitResult(): void {
    if (!this.activeMatch || this.winnerTeamId == null) {
      this.error = 'Pick a winner.';
      return;
    }
    const scoresArr = this.sets
      .filter(s => s.a != null && s.b != null)
      .map(s => ({ a: s.a, b: s.b }));
    const body = {
      winner_team_id: this.winnerTeamId,
      scores_json: this.isWalkover || scoresArr.length === 0 ? undefined : JSON.stringify(scoresArr),
      is_walkover: this.isWalkover,
    };
    this.submitting = true;
    this.error = '';
    this.tournamentService.submitResult(this.activeMatch.id, body).subscribe({
      next: () => { this.submitting = false; this.activeMatch = null; this.loadBracket(); this.refreshStatus(); },
      error: err => { this.submitting = false; this.error = this.readError(err); },
    });
  }

  private refreshStatus(): void {
    this.tournamentService.getById(this.tournamentId).subscribe(t => { this.tournament = t; });
  }

  private readError(err: any): string {
    return typeof err?.error === 'string' ? err.error : 'Something went wrong. Please try again.';
  }
}
